import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SeV2AttachmentInputDto, SeV2AttachmentSource, bffApi } from "../../../autogen/bff-api";
import { DocumentUploadContext, useDocumentsUploader } from "../../documents/UppyUploader/useDocumentsUploader";
import { pollForUploadedDocumentId } from "../../documents/pollForUploadedDocumentId";
import { useAppDispatch } from "../../redux/hooks";
import { isAllowedAttachmentFile } from "./attachmentSource";

export interface SelectedAttachment {
  documentId: string;
  fileName: string;
  source: SeV2AttachmentSource;
}

export interface FailedUpload {
  id: string;
  fileName: string;
  file: File; // kept so the upload can be retried
}

// The subset of useMessageAttachments() that AttachmentPicker needs. The hook return satisfies this structurally
// (reset/toInputs/hasFailedUploads are extra), so callers pass the whole hook bundle while the picker stays decoupled.
export interface MessageAttachmentControls {
  attachments: SelectedAttachment[];
  failedUploads: FailedUpload[];
  isUploading: boolean;
  formatError: string | null;
  addFromSource: (documentId: string, fileName: string, source: SeV2AttachmentSource) => void;
  removeAttachment: (documentId: string) => void;
  uploadFiles: (files: File[]) => Promise<void>;
  retryFailedUpload: (id: string) => void;
  removeFailedUpload: (id: string) => void;
}

type SeV2UploadContext = Extract<
  DocumentUploadContext,
  "SeV2QuestionAttachment" | "SeV2AnswerAttachment" | "SeV2AnnouncementAttachment"
>;

interface Config {
  eventId: string;
  uploadEntityType: SeV2UploadContext;
  pollRole: "portal" | "buyer";
  questionId?: string;
}

export const useMessageAttachments = ({ eventId, uploadEntityType, pollRole, questionId }: Config) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [attachments, setAttachments] = useState<SelectedAttachment[]>([]);
  const [failedUploads, setFailedUploads] = useState<FailedUpload[]>([]);
  // A counter rather than a boolean, so concurrent uploads keep the spinner on until the last one settles.
  const [activeUploads, setActiveUploads] = useState(0);
  const isUploading = activeUploads > 0;
  const [formatError, setFormatError] = useState<string | null>(null);
  // Guards a double-click on Retry from re-enqueueing the same upload: the failedUploads closure is stale
  // between the click and the re-render that removes the chip.
  const retryingIds = useRef<Set<string>>(new Set());

  const { upload } = useDocumentsUploader({
    entityType: uploadEntityType,
    entityId: eventId,
    questionId,
    refetch: () => undefined,
  });

  const addFromSource = useCallback(
    (documentId: string, fileName: string, source: SeV2AttachmentSource) => {
      setAttachments((current) =>
        current.some((a) => a.documentId === documentId)
          ? current
          : [...current, { documentId, fileName, source }],
      );
    },
    [],
  );

  const removeAttachment = useCallback((documentId: string) => {
    setAttachments((current) => current.filter((a) => a.documentId !== documentId));
  }, []);

  const pollForDocumentId = useCallback(
    (uploadId: string): Promise<string | null> => {
      const endpoint =
        pollRole === "portal"
          ? bffApi.endpoints.getPortalSeV2AttachmentUpload
          : bffApi.endpoints.getBuyerSeV2AttachmentUpload;
      return pollForUploadedDocumentId(
        (uid) => {
          // Release the RTK Query subscription after each poll — otherwise ~30 forceRefetch dispatches per
          // upload accumulate subscriptions that keep the cache entry alive for the whole poll window.
          const subscription = dispatch(endpoint.initiate({ eventId, uploadId: uid }, { forceRefetch: true }));
          return subscription
            .unwrap()
            .then((result) => result.documentId ?? null)
            .catch(() => null)
            .finally(() => subscription.unsubscribe());
        },
        uploadId,
      );
    },
    [dispatch, eventId, pollRole],
  );

  const uploadFromDisk = useCallback(
    async (file: File) => {
      // Generate the id up front (before touching upload state) so a crypto failure can't leave the counter
      // incremented or the failure-tracking half-wired.
      const uploadId = crypto.randomUUID();
      setActiveUploads((n) => n + 1);
      try {
        const markFailed = () =>
          setFailedUploads((c) => [...c, { id: uploadId, fileName: file.name, file }]);

        const extension = file.name.includes(".") ? file.name.split(".").pop() ?? "" : "";
        const key = await upload({ id: uploadId, name: file.name, extension, file });
        if (!key) {
          markFailed();
          return;
        }
        const documentId = await pollForDocumentId(uploadId);
        if (documentId) {
          addFromSource(documentId, file.name, SeV2AttachmentSource.Upload);
        } else {
          markFailed();
        }
      } finally {
        setActiveUploads((n) => n - 1);
      }
    },
    [upload, pollForDocumentId, addFromSource],
  );

  // Validate the picked batch up front: upload the supported files and surface one error naming the rejected
  // ones, so a valid file in the same selection can't silently clear another file's "unsupported format" error.
  const uploadFiles = useCallback(
    async (files: File[]) => {
      const rejected = files.filter((f) => !isAllowedAttachmentFile(f.name));
      setFormatError(
        rejected.length === 0
          ? null
          : t("These files were not added (unsupported format): {{names}}", {
              names: rejected.map((f) => f.name).join(", "),
            }),
      );
      const accepted = files.filter((f) => isAllowedAttachmentFile(f.name));
      await Promise.all(accepted.map((file) => uploadFromDisk(file)));
    },
    [uploadFromDisk, t],
  );

  const removeFailedUpload = useCallback((id: string) => {
    setFailedUploads((current) => current.filter((f) => f.id !== id));
  }, []);

  const retryFailedUpload = useCallback(
    (id: string): Promise<void> => {
      if (retryingIds.current.has(id)) return Promise.resolve();
      const entry = failedUploads.find((f) => f.id === id);
      if (!entry) return Promise.resolve();
      retryingIds.current.add(id);
      setFailedUploads((current) => current.filter((f) => f.id !== id));
      return uploadFromDisk(entry.file).finally(() => retryingIds.current.delete(id));
    },
    [failedUploads, uploadFromDisk],
  );

  const reset = useCallback(() => {
    setAttachments([]);
    setFailedUploads([]);
    setFormatError(null);
  }, []);

  const toInputs = useCallback(
    (): SeV2AttachmentInputDto[] => attachments.map((a) => ({ documentId: a.documentId, source: a.source })),
    [attachments],
  );

  return {
    attachments,
    failedUploads,
    addFromSource,
    removeAttachment,
    uploadFromDisk,
    uploadFiles,
    retryFailedUpload,
    removeFailedUpload,
    isUploading,
    hasFailedUploads: failedUploads.length > 0,
    formatError,
    reset,
    toInputs,
  };
};
